import React from 'react';
import { FileText, CheckCircle, Clock, Package, AlertTriangle, TrendingUp } from 'lucide-react';
import { User, DeploymentRequest } from '../../types';
import StatsCard from './StatsCard';
import DeploymentCard from '../Deployments/DeploymentCard';
import DeploymentChart from './DeploymentChart';
import DeploymentSummary from './DeploymentSummary';
import PredictiveAnalytics from '../AI/PredictiveAnalytics';
import SmartRecommendations from '../AI/SmartRecommendations';
import AIInsightsBanner from '../AI/AIInsightsBanner';
import DeploymentPunchCard from './DeploymentPunchCard';
import DeploymentStatusStrip from './DeploymentStatusStrip';
import DeploymentStreakTile from './DeploymentStreakTile';
import { generateMonthlyData, generateDailyData, generateModuleData } from '../../utils/dateUtils';

interface DashboardViewProps {
  currentUser: User;
  deployments: DeploymentRequest[];
}

const DashboardView: React.FC<DashboardViewProps> = ({ currentUser, deployments }) => {
  const getStatsForRole = () => {
    // Filter to get user's own requests for TL and PM
    const myRequests = deployments.filter(d => d.requestedBy?.id === currentUser.id);
    
    const total = deployments.length;
    const pending = deployments.filter(d => d.status === 'pending').length;
    const tlApproved = deployments.filter(d => d.status === 'tl_approved').length;
    const pmApproved = deployments.filter(d => d.status === 'pm_approved').length;
    const deployed = deployments.filter(d => d.status === 'deployed').length;
    const rolledBack = deployments.filter(d => (d.status as string) === 'rolled_back').length;
    // Of everything that went live, how much stayed live (not rolled back)
    const successRate = deployed + rolledBack > 0
      ? `${Math.round((deployed / (deployed + rolledBack)) * 100)}%`
      : '—';

    const baseStats = [
      { title: 'Total Requests', value: total, icon: FileText, color: 'blue' as const },
      { title: 'Deployed', value: deployed, icon: CheckCircle, color: 'green' as const },
    ];

    switch (currentUser.role) {
      case 'developer':
        const myPendingApproval = myRequests.filter(d => 
          d.status && ['pending', 'tl_approved', 'pm_approved'].includes(d.status)
        ).length;
        return [
          { title: 'My Requests', value: myRequests.length, icon: FileText, color: 'blue' as const },
          { title: 'Pending Approval', value: myPendingApproval, icon: Clock, color: 'yellow' as const },
          { title: 'Deployed', value: myRequests.filter(d => d.status === 'deployed').length, icon: CheckCircle, color: 'green' as const },
          { title: 'Rejected', value: myRequests.filter(d => d.status === 'rejected').length, icon: AlertTriangle, color: 'red' as const },
        ];
      case 'tl':
        return [
          { title: 'My Requests', value: myRequests.length, icon: FileText, color: 'blue' as const },
          { title: 'Deployed', value: myRequests.filter(d => d.status === 'deployed').length, icon: CheckCircle, color: 'green' as const },
          { title: 'Awaiting TL Approval', value: pending, icon: Clock, color: 'yellow' as const },
          { title: 'TL Approved', value: tlApproved, icon: CheckCircle, color: 'green' as const },
        ];
      case 'pm':
        return [
          { title: 'My Requests', value: myRequests.length, icon: FileText, color: 'blue' as const },
          { title: 'Deployed', value: myRequests.filter(d => d.status === 'deployed').length, icon: CheckCircle, color: 'green' as const },
          { title: 'Awaiting PM Approval', value: tlApproved, icon: Clock, color: 'yellow' as const },
          { title: 'PM Approved', value: pmApproved, icon: CheckCircle, color: 'green' as const },
        ];
      case 'devops':
        return [
          ...baseStats,
          { title: 'Ready to Deploy', value: pmApproved, icon: Package, color: 'purple' as const },
          { title: 'Success Rate', value: successRate, icon: TrendingUp, color: 'green' as const },
        ];
      case 'tester':
        return [
          ...baseStats,
          { title: 'Pending', value: pending, icon: Clock, color: 'yellow' as const },
          { title: 'In Progress', value: tlApproved + pmApproved, icon: Package, color: 'purple' as const },
        ];
      default:
        return [
          ...baseStats,
          { title: 'Pending', value: pending, icon: Clock, color: 'yellow' as const },
          { title: 'In Progress', value: tlApproved + pmApproved, icon: Package, color: 'purple' as const },
        ];
    }
  };

  const getRecentDeployments = () => {
    let filtered = deployments;
    
    if (currentUser.role === 'developer') {
      filtered = deployments.filter(d => d.requestedBy?.id === currentUser.id);
    }
    
    return filtered.slice(0, 5);
  };

  const stats = getStatsForRole();
  const recentDeployments = getRecentDeployments();
  const monthlyData = generateMonthlyData(deployments);
  const dailyData = generateDailyData(deployments);
  const moduleData = generateModuleData(deployments);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {currentUser.name}</p>
      </div>

      {/* Time-based Summary */}
      <DeploymentSummary deployments={deployments} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Charts Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Deployment Analytics</h2>
        <DeploymentChart
          monthlyData={monthlyData}
          dailyData={dailyData}
          moduleData={moduleData}
        />
      </div>

      {/* Activity Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Deployment Activity</h2>
        <div className="grid grid-cols-1 gap-6">
          <DeploymentPunchCard deployments={deployments} />
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <DeploymentStreakTile deployments={deployments} />
            </div>
            <div className="lg:col-span-3">
              <DeploymentStatusStrip deployments={deployments} />
            </div>
          </div>
        </div>
      </div>

      {/* AI-Powered Insights */}
      <div className="space-y-4">
        <AIInsightsBanner />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PredictiveAnalytics currentUser={currentUser} deployments={deployments} />
          <SmartRecommendations currentUser={currentUser} deployments={deployments} />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Deployments</h2>
        </div>
        <div className="p-6 space-y-4">
          {recentDeployments.map((deployment) => (
            <DeploymentCard
              key={deployment.id}
              deployment={deployment}
              currentUser={currentUser}
              onAction={() => {}}
              showActions={false}
            />
          ))}
          {recentDeployments.length === 0 && (                                                                                                                                                                                                                                                                                        
            <p className="text-gray-500 text-center py-8">No recent deployments</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardView;